import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../../api";
import usePageTitle from "../../../hooks/usePageTitle";
import AppShell from "../../AppShell";
import ImageWithFallback from "../../ImageWithFallback";
import { buildItemUrl } from "../../../utils/itemUrl";
import { formatPrice } from "../../../utils/price";
import { PriceVisibilityBoundary } from "../../../contexts/PriceVisibilityContext";

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const interactiveCardSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "rgba(39, 18, 68, 0.10)",
  backgroundColor: "#fffafd",
  transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit",
  display: "block",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 34px rgba(59, 18, 74, 0.12)",
    borderColor: "rgba(181, 75, 141, 0.26)",
    backgroundColor: "#ffffff",
  },
};

const statCardSx = {
  p: { xs: 1.5, md: 1.75 },
  borderRadius: 2,
  border: "1px solid",
  borderColor: "rgba(39, 18, 68, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
};

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function containsText(value, needle) {
  return normalizeText(value).includes(normalizeText(needle));
}

function isSecretRareLike(item) {
  const rarity = normalizeText(item?.rarity);
  return rarity === "secret rare" || rarity === "sec";
}

function isRegularVariant(item) {
  const variant = normalizeText(item?.edition_variant);
  return !variant || variant === "regular";
}

function isUtaMangaSEC(item) {
  return (
    String(item?.number || "").trim() === "EB03-061" &&
    isSecretRareLike(item) &&
    containsText(item?.edition_variant, "manga")
  );
}

function isUtaRegularSEC(item) {
  return (
    String(item?.number || "").trim() === "EB03-061" &&
    isSecretRareLike(item) &&
    isRegularVariant(item)
  );
}

function isUtaAltArtSEC(item) {
  return Number(item?.item_id) === 141;
}

function isUtaAltArtSECFallback(item) {
  return (
    String(item?.number || "").trim() === "EB03-061" &&
    isSecretRareLike(item) &&
    containsText(item?.edition_variant, "alternate")
  );
}

function isLawRegularSEC(item) {
  return (
    String(item?.number || "").trim() === "EB03-062" &&
    isSecretRareLike(item) &&
    isRegularVariant(item)
  );
}

function isLawAltArtSEC(item) {
  return (
    String(item?.number || "").trim() === "EB03-062" &&
    isSecretRareLike(item) &&
    containsText(item?.edition_variant, "alternate")
  );
}

function getPrimaryImage(item) {
  return item?.image_url || item?.ref_image_url || "";
}

function SectionBlock({ title, subtitle, accent = false, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        borderColor: accent ? "rgba(181, 75, 141, 0.18)" : panelSx.borderColor,
        boxShadow: accent ? "0 18px 40px rgba(59, 18, 74, 0.10)" : panelSx.boxShadow,
      }}
    >
      <Box sx={{ mb: 2.25 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: "#2d163f", mb: 0.65 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 860, lineHeight: 1.65 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

function PlaceholderImage() {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 150,
        borderRadius: 1.75,
        border: "1px solid",
        borderColor: "rgba(39, 18, 68, 0.08)",
        backgroundColor: "#f7eff7",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      No Image
    </Box>
  );
}

function EventItemCard({ item, emphasize = false }) {
  const imageSrc = getPrimaryImage(item);
  const itemUrl = buildItemUrl(item);
  const formattedPrice = formatPrice(item);
  const rarityLabel =
    normalizeText(item?.rarity) === "don!!" ? "Gold DON!!!" : item?.rarity;

  return (
    <Box
      component={RouterLink}
      to={itemUrl}
      sx={{
        ...interactiveCardSx,
        p: { xs: 1.2, md: 1.35 },
        height: "100%",
        borderColor: emphasize ? "rgba(181, 75, 141, 0.22)" : interactiveCardSx.borderColor,
        backgroundColor: emphasize ? "#fff8fc" : interactiveCardSx.backgroundColor,
      }}
    >
      <Box
        sx={{
          height: emphasize ? 160 : 148,
          borderRadius: 1.75,
          overflow: "hidden",
          mb: 1.1,
          backgroundColor: "#f7eff7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imageSrc ? (
          <ImageWithFallback
            src={item.image_url}
            fallbackSrcs={[item.ref_image_url]}
            alt={item.name || "EB-03 item"}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <PlaceholderImage />
        )}
      </Box>

      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 0.9 }}>
        {rarityLabel && (
          <Chip
            label={rarityLabel}
            size="small"
            sx={{
              fontWeight: 700,
              height: 24,
              backgroundColor: "rgba(181, 75, 141, 0.12)",
              color: "#7b255c",
            }}
          />
        )}
        {item?.edition_variant && (
          <Chip
            label={item.edition_variant}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 700,
              height: 24,
              borderColor: "rgba(39, 18, 68, 0.10)",
              backgroundColor: "#ffffff",
            }}
          />
        )}
      </Stack>

      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          color: "#2d163f",
          lineHeight: 1.35,
          mb: 0.55,
          minHeight: 40,
        }}
      >
        {item?.name || `Item #${item?.item_id}`}
      </Typography>

      <Stack spacing={0.35}>
        {item?.number && (
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
            No. {item.number}
          </Typography>
        )}
        {item?.edition_variant && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {item.edition_variant}
          </Typography>
        )}
        {formattedPrice && <PriceVisibilityBoundary>
          <Typography variant="body2" sx={{ color: "#7b255c", fontWeight: 800, pt: 0.4 }}>
            {formattedPrice}
          </Typography>
        </PriceVisibilityBoundary>}
      </Stack>
    </Box>
  );
}

function assignOrdered(items, assignedIds, sectionMap, sectionKey, matchers) {
  matchers.forEach((matcher) => {
    const match = items.find((item) => {
      const itemId = Number(item?.item_id);
      if (!Number.isFinite(itemId) || assignedIds.has(itemId)) return false;
      return matcher(item);
    });
    if (match) {
      assignedIds.add(Number(match.item_id));
      sectionMap[sectionKey].push(match);
    }
  });
}

function assignSections(items) {
  const assignedIds = new Set();
  const sectionMap = {
    topChases: [],
    leaders: [],
    spCards: [],
    secretRares: [],
    alternateArts: [],
    donCards: [],
    standardCards: [],
  };

  const tryAssign = (key, predicate) => {
    items.forEach((item) => {
      const itemId = Number(item?.item_id);
      if (!Number.isFinite(itemId) || assignedIds.has(itemId)) return;
      if (!predicate(item)) return;
      assignedIds.add(itemId);
      sectionMap[key].push(item);
    });
  };

  const hasUtaAltArt141 = items.some((item) => Number(item?.item_id) === 141);

  assignOrdered(items, assignedIds, sectionMap, "topChases", [
    isUtaMangaSEC,
    isUtaRegularSEC,
    hasUtaAltArt141 ? isUtaAltArtSEC : isUtaAltArtSECFallback,
    isLawRegularSEC,
    isLawAltArtSEC,
  ]);
  tryAssign("leaders", (item) => normalizeText(item?.rarity) === "leader");
  tryAssign("spCards", (item) => containsText(item?.edition_variant, "sp"));
  tryAssign("secretRares", (item) => isSecretRareLike(item));
  tryAssign("alternateArts", (item) => containsText(item?.edition_variant, "alternate art"));
  tryAssign("donCards", (item) => normalizeText(item?.rarity) === "don!!");
  tryAssign("standardCards", () => true);

  return sectionMap;
}

export default function EB03ReleasePage() {
  usePageTitle("EB-03: One Piece Heroines Edition");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadItems = async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await api.get("/api/events/eb03-release/items");
        if (!mounted) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load EB-03 items.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadItems();
    return () => {
      mounted = false;
    };
  }, []);

  const overviewStats = useMemo(() => {
    return {
      total: items.length,
      sp: items.filter((item) => containsText(item?.edition_variant, "sp")).length,
      altArts: items.filter((item) => containsText(item?.edition_variant, "alternate art")).length,
      leaders: items.filter((item) => normalizeText(item?.rarity) === "leader").length,
      don: items.filter((item) => normalizeText(item?.rarity) === "don!!").length,
    };
  }, [items]);

  const sections = useMemo(() => assignSections(items), [items]);

  const sectionConfigs = [
    {
      key: "topChases",
      title: "Top Chases",
      subtitle: "The headline cards driving release-week attention across manga variants, secret rares, and the Trafalgar Law chase pieces already mapped to this event.",
      items: sections.topChases,
      accent: true,
      emphasize: true,
    },
    {
      key: "leaders",
      title: "Leaders",
      subtitle: "Leader cards grouped first so alternate art leaders and mainline leaders stay easy to review.",
      items: sections.leaders,
    },
    {
      key: "spCards",
      title: "SP Cards",
      subtitle: "Premium spotlight cards with collector-first presentation and strong chase appeal.",
      items: sections.spCards,
    },
    {
      key: "secretRares",
      title: "Secret Rares",
      subtitle: "Scarcer pulls from the set that anchor high-end collector attention.",
      items: sections.secretRares,
    },
    {
      key: "alternateArts",
      title: "Alternate Arts",
      subtitle: "Alternate art versions that remain after leaders and top chase tiers are carved out above.",
      items: sections.alternateArts,
    },
    {
      key: "donCards",
      title: "Gold DON!!!",
      subtitle: "Gold DON!!! cards grouped separately for quick collector review.",
      items: sections.donCards,
    },
    {
      key: "standardCards",
      title: "Standard Cards",
      subtitle: "Everything else from Heroines Edition that was not already surfaced in a higher-priority section.",
      items: sections.standardCards,
    },
  ];

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
        <Stack spacing={{ xs: 2.5, md: 3.5 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3.25 },
              position: "relative",
              overflow: "hidden",
              background: "linear-gradient(135deg, #3d183f 0%, #7b255c 55%, #d99a53 100%)",
              borderColor: "rgba(255,255,255,0.10)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at top left, rgba(255,255,255,0.16), transparent 30%), radial-gradient(circle at bottom right, rgba(255,255,255,0.10), transparent 26%)",
                pointerEvents: "none",
              }}
            />
            <Grid container spacing={2.5} alignItems="flex-end" sx={{ position: "relative", zIndex: 1 }}>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    color: "rgba(255,255,255,0.68)",
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    mb: 1,
                  }}
                >
                  Set Release Showcase
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 900,
                    lineHeight: 1.06,
                    mb: 1.1,
                    maxWidth: 760,
                    fontSize: { xs: "2rem", md: "2.7rem" },
                  }}
                >
                  EB-03: One Piece Heroines Edition
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.82)",
                    maxWidth: 760,
                    lineHeight: 1.65,
                    fontSize: { xs: "0.98rem", md: "1.02rem" },
                    mb: 2,
                  }}
                >
                  A collector-focused view of the biggest chase cards, alternate arts, SPs, leaders, and DON!! cards from Heroines Edition.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {["One Piece", "New Release", "Chase Cards", "Heroines Edition"].map((chip) => (
                    <Chip
                      key={chip}
                      label={chip}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: "rgba(255,255,255,0.12)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.16)",
                      }}
                    />
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.10)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.66)", fontWeight: 800 }}>
                    Release Focus
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.88)", lineHeight: 1.65 }}>
                    Built around rarity, showcase variants, and chase-tier card types so the page feels closer to a premium set dashboard than a generic market grid.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Paper elevation={0} sx={{ ...panelSx, py: { xs: 5, md: 7 } }}>
              <Stack spacing={1.5} alignItems="center" justifyContent="center">
                <CircularProgress size={34} />
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Loading Heroines Edition items...
                </Typography>
              </Stack>
            </Paper>
          ) : error ? (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            </Paper>
          ) : items.length === 0 ? (
            <Paper elevation={0} sx={panelSx}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#2d163f", mb: 0.5 }}>
                No EB-03 items found
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                There are currently no mapped items for this event.
              </Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={1.5}>
                {[
                  { label: "Chase Cards", value: 41 },
                  { label: "SP Cards", value: overviewStats.sp },
                  { label: "Alternate Arts", value: overviewStats.altArts },
                  { label: "Leaders", value: overviewStats.leaders },
                  { label: "Gold DON!!!", value: overviewStats.don },
                ].map((stat) => (
                  <Grid item xs={6} sm={4} md key={stat.label}>
                    <Paper elevation={0} sx={statCardSx}>
                      <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 800 }}>
                        {stat.label}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: "#2d163f", mt: 0.4 }}>
                        {stat.value}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Stack spacing={{ xs: 2.5, md: 3 }}>
                {sectionConfigs.map((section) => {
                  if (!section.items?.length) return null;

                  return (
                    <SectionBlock
                      key={section.key}
                      title={section.title}
                      subtitle={section.subtitle}
                      accent={section.accent}
                    >
                      <Grid container spacing={1.5}>
                        {section.items.map((item) => (
                          <Grid item xs={6} sm={4} md={3} lg={2} key={item.item_id}>
                            <EventItemCard item={item} emphasize={Boolean(section.emphasize)} />
                          </Grid>
                        ))}
                      </Grid>
                    </SectionBlock>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </AppShell>
  );
}
